import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Search, Users, Building2, Factory, ShieldCheck, CheckCircle2, Clock, Ban } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { adminApi, formatApiErrorDetail, getUser } from "../../lib/authApi";
import { LoginBadge, RoleBadge, StatusBadge, fmtDate } from "./accountShared";
import { AccountActionsMenu } from "./AccountActionsMenu";
import { AccountDetailSheet } from "./AccountDetailSheet";
import { AddAccountDialog } from "./AddAccountDialog";
import { ConfirmAction, ResetLinkDialog } from "./AccountDialogs";
import { useAccountActions } from "./useAccountActions";

const CARDS = [["total", "Total Users", Users], ["customers", "Customers", Building2], ["partners", "Partners", Factory], ["admins", "VECTRON Admins", ShieldCheck], ["active", "Active", CheckCircle2], ["pending", "Pending", Clock], ["suspended_disabled", "Suspended / Disabled", Ban]];
const ANY = "any";

const FilterSelect = ({ value, onChange, options, placeholder, testId, className = "w-full sm:w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-${v}`}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const AdminAccountsSection = () => {
  const self = getUser();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [login, setLogin] = useState(ANY);
  const [partnerStatus, setPartnerStatus] = useState(ANY);
  const [sort, setSort] = useState("created:desc");
  const [adding, setAdding] = useState(false);
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    const [sortKey, order] = sort.split(":");
    const params = { q, role: role === ANY ? "" : role, status: status === ANY ? "" : status, login_status: login === ANY ? "" : login, partner_status: partnerStatus === ANY ? "" : partnerStatus, sort: sortKey, order };
    try {
      const [s, l] = await Promise.all([adminApi.get("/admin/accounts/summary"), adminApi.get("/admin/accounts", { params })]);
      setSummary(s.data);
      setItems(l.data.items);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setItems([]);
    }
  }, [q, role, status, login, partnerStatus, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const actions = useAccountActions(() => { load(); setRefreshKey((k) => k + 1); });
  const onAction = (kind, user, extra) => (kind === "refresh" ? load() : actions.run(kind, user, extra));
  const openDetail = (user, tab = "overview") => setDetail({ id: user.id, tab });
  const showPartnerFilter = useMemo(() => role === "partner" || role === ANY, [role]);

  return (
    <div className="space-y-6" data-testid="admin-accounts-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="accounts-summary">
        {CARDS.map(([k, label, Icon]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4" data-testid={`accounts-card-${k}`}>
            <div className="flex items-center justify-between text-muted-foreground"><span className="text-[11px] uppercase tracking-widest">{label}</span><Icon className="h-4 w-4" strokeWidth={1.5} /></div>
            <p className="mt-2 text-2xl font-heading font-semibold" data-testid={`accounts-card-${k}-value`}>{summary ? summary[k] : "—"}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-testid="accounts-toolbar">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or company…" className="pl-9" data-testid="accounts-search" />
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <FilterSelect value={role} onChange={setRole} testId="accounts-filter-role" placeholder="Role" options={[[ANY, "All roles"], ["customer", "Customer"], ["partner", "Partner"], ["admin", "VECTRON Admin"]]} />
          <FilterSelect value={status} onChange={setStatus} testId="accounts-filter-status" placeholder="Account status" options={[[ANY, "All statuses"], ["active", "Active"], ["pending", "Pending"], ["suspended", "Suspended"], ["disabled", "Disabled"], ["archived", "Archived"]]} />
          <FilterSelect value={login} onChange={setLogin} testId="accounts-filter-login" placeholder="Login status" options={[[ANY, "Any login status"], ["active_24h", "Logged in <24h"], ["recent_30d", "Logged in <30d"], ["inactive", "Inactive >30d"], ["never", "Never logged in"], ["locked", "Locked"]]} className="w-full sm:w-44" />
          {showPartnerFilter && <FilterSelect value={partnerStatus} onChange={setPartnerStatus} testId="accounts-filter-partner" placeholder="Partner approval" options={[[ANY, "Any approval"], ["pending", "Approval pending"], ["approved", "Approved"], ["rejected", "Rejected"]]} className="w-full sm:w-44" />}
          <FilterSelect value={sort} onChange={setSort} testId="accounts-sort" placeholder="Sort" options={[["created:desc", "Newest created"], ["created:asc", "Oldest created"], ["last_login:desc", "Last login · recent"], ["last_login:asc", "Last login · oldest"], ["name:asc", "Name A–Z"]]} className="w-full sm:w-44" />
        </div>
        <Button onClick={() => setAdding(true)} className="rounded-full" data-testid="accounts-add-btn"><Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Add Account</Button>
      </div>

      {!items ? <div className="py-12 flex justify-center" data-testid="accounts-loading"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="accounts-table-wrap">
          <Table data-testid="accounts-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Account Status</TableHead><TableHead>Login Status</TableHead><TableHead>Last Login</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10" data-testid="accounts-empty">No accounts match these filters.</TableCell></TableRow>}
              {items.map((u) => (
                <TableRow key={u.id} className="cursor-pointer" onClick={() => openDetail(u)} data-testid={`account-row-${u.id}`}>
                  <TableCell className="font-medium whitespace-nowrap" data-testid={`account-name-${u.id}`}>{u.name || "—"}{u.id === self?.id && <span className="ml-2 text-[10px] uppercase tracking-widest text-primary">you</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground" data-testid={`account-email-${u.id}`}>{u.email}</TableCell>
                  <TableCell><RoleBadge role={u.role} testId={`account-role-${u.id}`} /></TableCell>
                  <TableCell><StatusBadge status={u.account_status} testId={`account-status-${u.id}`} />{u.role === "partner" && u.partner_status && u.partner_status !== "approved" && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-600" data-testid={`account-partner-status-${u.id}`}>{u.partner_status}</span>}</TableCell>
                  <TableCell><LoginBadge status={u.login_status} testId={`account-login-${u.id}`} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(u.last_login_at)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}><AccountActionsMenu user={u} onView={openDetail} onAction={onAction} isSelf={u.id === self?.id} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddAccountDialog open={adding} onClose={() => setAdding(false)} onCreated={(data) => { setAdding(false); load(); actions.showReset({ ...data.invite, email: data.user.email }); }} />
      <AccountDetailSheet userId={detail?.id} tab={detail?.tab} onTab={(t) => setDetail((d) => ({ ...d, tab: t }))} onClose={() => setDetail(null)} onAction={onAction} refreshKey={refreshKey} selfId={self?.id} />
      <ConfirmAction action={actions.pending} busy={actions.busy} onCancel={actions.cancel} onConfirm={actions.confirm} />
      <ResetLinkDialog reset={actions.reset} onClose={actions.closeReset} />
    </div>
  );
};
